import { useRef } from 'react';
import { useRecurly } from '@recurly/react-recurly';
import { FormikHelpers, FormikProps, FormikValues } from 'formik';
import { useTranslation } from '@blocs.i18n';
import { captureExceptionEvent } from '@blocs.observability';
import { TokenPayload } from '@recurly/recurly-js';
import { PaymentMethodFormType } from './Checkout/types';
import { UseBilling } from './useBilling';
import { PAYMENT_METHOD_TYPE } from './enums/PaymentMethods';

interface UsePaymentMethodFormParams {
  onUpdatePaymentOption?: UseBilling['onUpdatePaymentOption'];
  setSelectedCard?: (card: UseBilling['wallet'][0]) => void;
}

// Recurly's field-level validation errors (err.details[].field) are keyed by its own hosted-field
// names (see data-recurly attributes in BillingAddressFormLayout/CreditCardDetailsFormLayout), which
// don't all match this form's Formik field names.
const RECURLY_FIELD_TO_FORM_FIELD: Record<string, string> = {
  state: 'state_region',
  address1: 'address_line_1',
  address2: 'address_line_2',
};

// Per-field inline copy for the address fields Recurly validates server-side (AC2). Fields not
// listed here (card number, cvv, expiry, name) keep the prior generic/raw-message behavior below.
const RECURLY_FIELD_ERROR_MESSAGE_KEYS: Record<string, string> = {
  // eslint-disable-next-line camelcase
  postal_code: 'common:validation.invalidZipPostalCode',
  city: 'common:validation.invalidCity',
  state: 'common:validation.invalidStateProvince',
  address1: 'common:validation.invalidAddress',
  address2: 'common:validation.invalidAddress',
  country: 'common:validation.invalidCountry',
};

export const getPaymentMethodPriorities = (values?: PaymentMethodFormType) => {
  const paymentPriority = values?.paymentPriority || [];
  return {
    isBackupPaymentMethod: paymentPriority.some(
      (priorityString: string) => priorityString === PAYMENT_METHOD_TYPE.BACKUP
    ),
    isPrimaryPaymentMethod: paymentPriority.some(
      (priorityString: string) => priorityString === PAYMENT_METHOD_TYPE.PRIMARY
    ),
  };
};

// No priority checkboxes are ever shown while adding a new card, so values.paymentPriority is always
// empty for that path — apply the wallet-empty-defaults-to-primary rule instead.
export const getNewPaymentMethodPriority = (values: PaymentMethodFormType | undefined, isWalletEmpty: boolean) =>
  isWalletEmpty ? { isPrimaryPaymentMethod: true, isBackupPaymentMethod: false } : getPaymentMethodPriorities(values);

// The checkout CTAs invoke their submit handlers directly rather than through Formik's own submit, so
// Formik never marks the fields touched. Do it here for anything invalid, which surfaces the inline
// error on the offending field instead of failing opaquely further down the submit path.
export const hasBlockingValidationErrors = async (
  formikHelpers: Pick<FormikHelpers<PaymentMethodFormType>, 'validateForm' | 'setTouched' | 'setSubmitting'>
): Promise<boolean> => {
  const invalidFields = Object.keys(await formikHelpers.validateForm());
  if (!invalidFields.length) return false;

  formikHelpers.setTouched(Object.fromEntries(invalidFields.map((field) => [field, true])));
  formikHelpers.setSubmitting(false);
  return true;
};

export const usePaymentMethodForm = ({
  onUpdatePaymentOption,
  setSelectedCard = () => null,
}: UsePaymentMethodFormParams) => {
  const formRef = useRef();
  const formikRef = useRef<FormikProps<PaymentMethodFormType>>();
  const recurly = useRecurly();
  const { t } = useTranslation();

  const requestRecurlyTokenAndValidate = (
    formData: FormikValues,
    formikHelpers: FormikHelpers<FormikValues>,
    onToken: (token: TokenPayload) => Promise<void>
  ) => {
    recurly.token(formRef.current, async (err, token) => {
      if (err) {
        if (['validation', 'invalid-parameter'].includes(err.code) && Array.isArray(err['details'])) {
          const fallbackMessage =
            err.code === 'invalid-parameter' ? err.message : t('common:validation.invalidFieldValue');
          err['details'].forEach((e) => {
            const messageKey = RECURLY_FIELD_ERROR_MESSAGE_KEYS[e.field];
            const errorMessage = messageKey ? t(messageKey) : fallbackMessage;
            formikHelpers.setFieldError(RECURLY_FIELD_TO_FORM_FIELD[e.field] ?? e.field, errorMessage);
          });
        } else {
          console.error(err);
          captureExceptionEvent(err);
        }
        // onSubmit hands back this (synchronous) call, so Formik never resolves the submit itself and
        // would otherwise leave the form stuck in its submitting state after a failed tokenization.
        formikHelpers.setSubmitting(false);
      } else {
        try {
          await onToken(token);
        } catch (error) {
          console.error('onAddPaymentOption error', error);
          throw error;
        }
      }
    });
  };

  const handlePaymentPriorityChange = async ({
    billingInfoId,
    isPrimaryPaymentMethod,
    isBackupPaymentMethod,
  }: {
    billingInfoId?: string;
    isPrimaryPaymentMethod?: boolean;
    isBackupPaymentMethod?: boolean;
  }) => {
    if (billingInfoId) {
      const updatedCard = await onUpdatePaymentOption({
        billingInfoId,
        isPrimaryPaymentMethod,
        isBackupPaymentMethod,
      });
      setSelectedCard(updatedCard);
    }
  };

  return {
    requestRecurlyTokenAndValidate,
    handlePaymentPriorityChange,
    paymentMethodFormRef: formRef,
    paymentMethodFormikRef: formikRef,
  };
};

export type UsePaymentMethodForm = ReturnType<typeof usePaymentMethodForm>;
